import { useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  DownloadSimple,
  File,
  Lock,
  Trash,
  Upload,
  Users,
} from "@phosphor-icons/react";

import api from "../lib/api";
import { useAuth } from "@/context/AuthContext";
import { PERMISSIONS } from "@/lib/permissions";

export default function Documentos() {
  const { can } = useAuth();

  const [tab, setTab] = useState("shared");
  const [sharedTab, setSharedTab] = useState("public");

  const [sharedDocs, setSharedDocs] = useState([]);
  const [myDocs, setMyDocs] = useState([]);
  const [familyDocs, setFamilyDocs] = useState([]);

  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  const sharedInputRef = useRef(null);
  const boardInputRef = useRef(null);
  const personalInputRef = useRef(null);

  const [search, setSearch] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [uploadModal, setUploadModal] = useState(null);

  const canManageDocuments = can(PERMISSIONS.MANAGE_DOCUMENTS);
  const canViewPrivate = can(PERMISSIONS.VIEW_PRIVATE_DOCUMENTS);
  const canManagePersonalDocuments = can(PERMISSIONS.MANAGE_PERSONAL_DOCUMENTS);

  const load = async () => {
    try {
      setLoading(true);

      const [sharedRes, myRes, familyRes] = await Promise.all([
        api.get("/documentos/shared"),
        api.get("/documentos/personal/mine"),
        api.get("/documentos/personal/family"),
      ]);

      setSharedDocs(sharedRes.data || []);
      setMyDocs(myRes.data || []);
      setFamilyDocs(familyRes.data || []);
    } catch (err) {
      console.error(err);

      toast.error("No se pudieron cargar los documentos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // -----------------------------------
  // UPLOAD
  // -----------------------------------

  const uploadShared = (files, visibility) => {
    setUploadModal({
      files: files.map((file) => ({
        file,
        titulo: "",
      })),
      type: "shared",
      visibility,
    });
  };

  const uploadPersonal = (files) => {
    setUploadModal({
      files: files.map((file) => ({
        file,
        titulo: "",
      })),
      type: "personal",
    });
  };

  const confirmUpload = async () => {
    if (!uploadModal?.files?.length) {
      return;
    }

    const hasEmptyTitle = uploadModal.files.some((item) => !item.titulo.trim());

    if (hasEmptyTitle) {
      toast.error("Introduce un título para todos los documentos");
      return;
    }

    try {
      setUploading(true);

      for (const item of uploadModal.files) {
        const form = new FormData();

        form.append("titulo", item.titulo.trim());

        if (uploadModal.type === "shared") {
          form.append("visibility", uploadModal.visibility);
        }

        form.append("file", item.file);

        await api.post(
          uploadModal.type === "shared"
            ? "/documentos/shared"
            : "/documentos/personal",
          form,
        );
      }

      toast.success(
        uploadModal.files.length > 1
          ? "Documentos subidos"
          : "Documento subido",
      );

      setUploadModal(null);
      load();
    } catch (err) {
      console.error(err);

      toast.error(
        err?.response?.data?.detail || "No se pudo subir el documento",
      );
    } finally {
      setUploading(false);
    }
  };

  // -----------------------------------
  // DELETE
  // -----------------------------------

  const deleteShared = async (id) => {
    try {
      await api.delete(`/documentos/shared/${id}`);

      toast.success("Documento eliminado");

      load();
    } catch (err) {
      console.error(err);

      toast.error(err?.response?.data?.detail || "No se pudo eliminar");
    }
  };

  const deletePersonal = async (id) => {
    try {
      await api.delete(`/documentos/personal/${id}`);

      toast.success("Documento eliminado");

      load();
    } catch (err) {
      console.error(err);

      toast.error(err?.response?.data?.detail || "No se pudo eliminar");
    }
  };

  // -----------------------------------
  // HELPERS
  // -----------------------------------

  const formatSize = (bytes) => {
    if (!bytes) {
      return "";
    }

    if (bytes < 1024) {
      return `${bytes} B`;
    }

    if (bytes < 1024 * 1024) {
      return `${(bytes / 1024).toFixed(1)} KB`;
    }

    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  };

  const formatDate = (value) =>
    new Date(value).toLocaleDateString("es-ES", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });

  const isImage = (doc) => doc.mime_type?.startsWith("image/");

  const publicDocs = useMemo(
    () => sharedDocs.filter((doc) => doc.visibility === "public"),
    [sharedDocs],
  );

  const boardDocs = useMemo(
    () => sharedDocs.filter((doc) => doc.visibility === "board"),
    [sharedDocs],
  );

  const canDeleteDocument = (personal, source) => {
    if (!personal) {
      return canManageDocuments;
    }

    if (source === "mine") {
      return true;
    }

    return canManagePersonalDocuments;
  };

  const renderDocumentCard = (
    doc,
    { personal = false, source = null } = {},
  ) => {
    const imagePreview = isImage(doc);

    const canDelete = canDeleteDocument(personal, source);

    const handleDelete = () => {
      setDeleteTarget({
        id: doc.id,
        personal,
      });
    };

    if (imagePreview) {
      return (
        <motion.div
          key={doc.id}
          layout
          className="overflow-hidden rounded-2xl border border-med-border bg-med-surface"
        >
          <div className="relative">
            <img
              src={doc.download_url}
              alt={doc.titulo}
              className="h-72 w-full object-cover"
            />

            <div className="absolute top-3 right-3 flex items-center gap-2">
              <a
                href={doc.download_url}
                target="_blank"
                rel="noreferrer"
                className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/90 backdrop-blur transition-colors hover:bg-white"
              >
                <DownloadSimple size={18} />
              </a>

              {canDelete && (
                <button
                  onClick={handleDelete}
                  className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/90 text-red-500 backdrop-blur transition-colors hover:bg-white"
                >
                  <Trash size={18} />
                </button>
              )}
            </div>
          </div>

          <div className="p-5">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="truncate font-semibold text-med-text">
                  {doc.titulo}
                </div>

                <div className="mt-2 flex flex-wrap gap-3 text-sm text-med-muted">
                  {doc.size && <span>{formatSize(doc.size)}</span>}

                  {doc.created_at && <span>{formatDate(doc.created_at)}</span>}
                </div>

                {doc.fallero_nombre && (
                  <div className="mt-3 text-sm text-med-muted">
                    {doc.fallero_nombre}
                  </div>
                )}
              </div>

              <div className="flex flex-wrap items-center gap-2">
                {doc.visibility === "public" && (
                  <span className="inline-flex items-center rounded-full bg-med-surface2 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-med-muted">
                    Público
                  </span>
                )}

                {doc.visibility === "board" && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-amber-700">
                    <Lock size={12} />
                    Directiva
                  </span>
                )}

                {personal && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-blue-700">
                    <Users size={12} />
                    Familiar
                  </span>
                )}
              </div>
            </div>
          </div>
        </motion.div>
      );
    }

    return (
      <motion.div
        key={doc.id}
        layout
        className="rounded-2xl border border-med-border bg-med-surface p-5"
      >
        <div className="flex items-start justify-between gap-4">
          <div className="flex min-w-0 gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-med-surface2">
              <File size={24} weight="duotone" className="text-med-primary" />
            </div>

            <div className="min-w-0">
              <div className="truncate font-semibold text-med-text">
                {doc.titulo}
              </div>

              <div className="mt-1 truncate text-sm text-med-muted">
                {doc.original_filename}
              </div>

              <div className="mt-3 flex flex-wrap gap-3 text-xs text-med-muted">
                {doc.size && <span>{formatSize(doc.size)}</span>}

                {doc.created_at && <span>{formatDate(doc.created_at)}</span>}

                {doc.fallero_nombre && <span>{doc.fallero_nombre}</span>}
              </div>

              <div className="mt-3 flex flex-wrap items-center gap-2">
                {doc.visibility === "public" && (
                  <span className="inline-flex items-center rounded-full bg-med-surface2 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-med-muted">
                    Público
                  </span>
                )}

                {doc.visibility === "board" && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-amber-700">
                    <Lock size={12} />
                    Directiva
                  </span>
                )}

                {personal && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-blue-700">
                    <Users size={12} />
                    Familiar
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="flex shrink-0 items-center gap-2">
            <a
              href={doc.download_url}
              target="_blank"
              rel="noreferrer"
              className="flex h-9 w-9 items-center justify-center rounded-lg bg-med-surface2 transition-colors hover:bg-med-surface"
            >
              <DownloadSimple size={18} />
            </a>

            {canDelete && (
              <button
                onClick={handleDelete}
                className="flex h-9 w-9 items-center justify-center rounded-lg bg-red-50 text-red-500 transition-colors hover:bg-red-100"
              >
                <Trash size={18} />
              </button>
            )}
          </div>
        </div>
      </motion.div>
    );
  };

  const filterDocuments = (documents) =>
    documents.filter((doc) =>
      doc.titulo?.toLowerCase().includes(search.toLowerCase()),
    );

  return (
    <div className="p-6 md:p-10">
      <header className="mb-8">
        <div className="mb-2 text-xs font-semibold uppercase tracking-[0.2em] text-med-muted">
          Gestión
        </div>

        <h1 className="font-display text-4xl font-bold text-med-text">
          Documentos
        </h1>

        <p className="mt-2 text-med-muted">
          Gestiona documentos públicos, internos y personales.
        </p>
      </header>

      <div className="mb-6 flex flex-wrap gap-2">
        <button
          onClick={() => setTab("shared")}
          className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold transition-colors ${
            tab === "shared"
              ? "bg-med-primary text-white"
              : "border border-med-border bg-med-surface text-med-text hover:border-med-primary"
          }`}
        >
          Compartidos
        </button>

        <button
          onClick={() => setTab("personal")}
          className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold transition-colors ${
            tab === "personal"
              ? "bg-med-primary text-white"
              : "border border-med-border bg-med-surface text-med-text hover:border-med-primary"
          }`}
        >
          Personales
        </button>
      </div>

      <div className="mb-8 flex flex-col gap-4">
        <input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Buscar documentos..."
          className="w-full max-w-md rounded-xl border border-med-border bg-med-surface px-4 py-3 text-med-text outline-none focus:border-med-primary"
        />
      </div>

      {tab === "shared" && (
        <div>
          <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setSharedTab("public")}
                className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold transition-colors ${
                  sharedTab === "public"
                    ? "bg-med-primary text-white"
                    : "border border-med-border bg-med-surface text-med-text hover:border-med-primary"
                }`}
              >
                Públicos
              </button>

              {canViewPrivate && (
                <button
                  onClick={() => setSharedTab("board")}
                  className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold transition-colors ${
                    sharedTab === "board"
                      ? "bg-med-primary text-white"
                      : "border border-med-border bg-med-surface text-med-text hover:border-med-primary"
                  }`}
                >
                  Directiva
                </button>
              )}
            </div>

            {canManageDocuments && (
              <div className="flex flex-wrap gap-2">
                <input
                  type="file"
                  multiple
                  hidden
                  ref={sharedInputRef}
                  onChange={(event) => {
                    const files = Array.from(event.target.files || []);

                    if (files.length) {
                      uploadShared(files, "public");
                    }

                    event.target.value = "";
                  }}
                />

                <button
                  disabled={uploading}
                  onClick={() => sharedInputRef.current?.click()}
                  className="inline-flex items-center gap-2 rounded-xl bg-med-primary px-4 py-2 text-sm font-semibold text-white transition-colors hover:opacity-90 disabled:opacity-50"
                >
                  <Upload size={18} />
                  Subir público
                </button>

                <input
                  type="file"
                  multiple
                  hidden
                  ref={boardInputRef}
                  onChange={(event) => {
                    const files = Array.from(event.target.files || []);

                    if (files.length) {
                      uploadShared(files, "board");
                    }

                    event.target.value = "";
                  }}
                />

                <button
                  disabled={uploading}
                  onClick={() => boardInputRef.current?.click()}
                  className="inline-flex items-center gap-2 rounded-xl border border-med-border bg-med-surface px-4 py-2 text-sm font-semibold text-med-text transition-colors hover:border-med-primary disabled:opacity-50"
                >
                  <Lock size={18} />
                  Subir directiva
                </button>
              </div>
            )}
          </div>

          {loading ? (
            <div className="rounded-2xl border border-med-border bg-med-surface p-6 text-med-muted">
              Cargando documentos...
            </div>
          ) : filterDocuments(sharedTab === "public" ? publicDocs : boardDocs)
              .length === 0 ? (
            <div className="rounded-2xl border border-med-border bg-med-surface p-6 text-med-muted">
              No hay documentos.
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
              {filterDocuments(
                sharedTab === "public" ? publicDocs : boardDocs,
              ).map((doc) => renderDocumentCard(doc))}
            </div>
          )}
        </div>
      )}

      {tab === "personal" && (
        <div className="space-y-8">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold text-med-text">
                Mis documentos
              </h2>

              <p className="mt-1 text-sm text-med-muted">
                Visibles para ti, tu familia y la directiva.
              </p>
            </div>

            <input
              type="file"
              multiple
              hidden
              ref={personalInputRef}
              onChange={(event) => {
                const files = Array.from(event.target.files || []);

                if (files.length) {
                  uploadPersonal(files);
                }

                event.target.value = "";
              }}
            />

            <button
              disabled={uploading}
              onClick={() => personalInputRef.current?.click()}
              className="inline-flex items-center gap-2 rounded-xl bg-med-primary px-4 py-2 text-sm font-semibold text-white transition-colors hover:opacity-90 disabled:opacity-50"
            >
              <Upload size={18} />
              Subir documento
            </button>
          </div>

          {loading ? (
            <div className="rounded-2xl border border-med-border bg-med-surface p-6 text-med-muted">
              Cargando documentos...
            </div>
          ) : filterDocuments(myDocs).length === 0 ? (
            <div className="rounded-2xl border border-med-border bg-med-surface p-6 text-med-muted">
              No has subido documentos.
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
              {filterDocuments(myDocs).map((doc) =>
                renderDocumentCard(doc, {
                  personal: true,
                  source: "mine",
                }),
              )}
            </div>
          )}

          <div>
            <h2 className="text-lg font-semibold text-med-text">
              Documentos familiares
            </h2>

            <p className="mt-1 text-sm text-med-muted">
              Documentos compartidos por familiares vinculados.
            </p>
          </div>

          {loading ? (
            <div className="rounded-2xl border border-med-border bg-med-surface p-6 text-med-muted">
              Cargando documentos...
            </div>
          ) : filterDocuments(familyDocs).length === 0 ? (
            <div className="rounded-2xl border border-med-border bg-med-surface p-6 text-med-muted">
              No hay documentos familiares.
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
              {filterDocuments(familyDocs).map((doc) =>
                renderDocumentCard(doc, {
                  personal: true,
                  source: "family",
                }),
              )}
            </div>
          )}
        </div>
      )}

      {/* ----------------------------------- */}
      {/* UPLOAD MODAL */}
      {/* ----------------------------------- */}

      {uploadModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            className="w-full max-w-2xl rounded-3xl border border-med-border bg-white p-8 shadow-2xl"
          >
            <div>
              <h3 className="text-2xl font-bold text-med-text">
                Título de los documentos
              </h3>

              <p className="mt-2 text-sm text-med-muted">
                Introduce el título que quieres mostrar para cada documento.
              </p>
            </div>

            <div className="mt-6 max-h-[60vh] space-y-4 overflow-y-auto pr-1">
              {uploadModal.files.map((item, index) => (
                <div
                  key={`${item.file.name}-${index}`}
                  className="rounded-2xl border border-med-border bg-med-surface p-4"
                >
                  <div className="mb-3 flex items-start gap-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-med-surface2">
                      <File
                        size={20}
                        weight="duotone"
                        className="text-med-primary"
                      />
                    </div>

                    <div className="min-w-0">
                      <div className="truncate text-sm font-semibold text-med-text">
                        {item.file.name}
                      </div>

                      <div className="mt-1 text-xs text-med-muted">
                        {formatSize(item.file.size)}
                      </div>
                    </div>
                  </div>

                  <input
                    autoFocus={index === 0}
                    value={item.titulo}
                    onChange={(event) => {
                      const titulo = event.target.value;

                      setUploadModal((current) => ({
                        ...current,
                        files: current.files.map((currentItem, currentIndex) =>
                          currentIndex === index
                            ? {
                                ...currentItem,
                                titulo,
                              }
                            : currentItem,
                        ),
                      }));
                    }}
                    placeholder="Título del documento"
                    className="w-full rounded-xl border border-med-border bg-white px-4 py-3 text-med-text outline-none focus:border-med-primary"
                  />
                </div>
              ))}
            </div>

            <div className="mt-8 flex justify-end gap-3">
              <button
                type="button"
                disabled={uploading}
                onClick={() => setUploadModal(null)}
                className="rounded-2xl border border-med-border px-5 py-2.5 text-sm font-semibold text-gray-700 transition-colors hover:bg-med-surface disabled:opacity-50"
              >
                Cancelar
              </button>

              <button
                type="button"
                disabled={
                  uploading ||
                  uploadModal.files.some((item) => !item.titulo.trim())
                }
                onClick={confirmUpload}
                className="rounded-2xl bg-med-primary px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:opacity-90 disabled:opacity-50"
              >
                {uploading
                  ? "Subiendo..."
                  : uploadModal.files.length > 1
                    ? "Subir documentos"
                    : "Subir documento"}
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* ----------------------------------- */}
      {/* DELETE MODAL */}
      {/* ----------------------------------- */}

      {deleteTarget && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
          <motion.div
            initial={{
              opacity: 0,
              scale: 0.95,
              y: 10,
            }}
            animate={{
              opacity: 1,
              scale: 1,
              y: 0,
            }}
            exit={{
              opacity: 0,
              scale: 0.95,
              y: 10,
            }}
            className="w-full max-w-lg rounded-3xl border border-med-border bg-white p-8 shadow-2xl"
          >
            <div className="flex items-start gap-4">
              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-red-100 text-red-500">
                <Trash size={28} weight="duotone" />
              </div>

              <div>
                <h3 className="text-2xl font-bold text-gray-700">
                  Eliminar documento
                </h3>

                <p className="mt-2 text-gray-600">
                  Esta acción eliminará el documento permanentemente y no se
                  puede deshacer.
                </p>
              </div>
            </div>

            <div className="mt-8 flex justify-end gap-3">
              <button
                onClick={() => setDeleteTarget(null)}
                className="rounded-2xl border border-med-border px-5 py-2.5 text-sm font-semibold text-gray-700 transition-colors hover:bg-med-surface"
              >
                Cancelar
              </button>

              <button
                onClick={async () => {
                  try {
                    if (deleteTarget.personal) {
                      await deletePersonal(deleteTarget.id);
                    } else {
                      await deleteShared(deleteTarget.id);
                    }

                    setDeleteTarget(null);
                  } catch {
                    // El error ya se muestra en
                    // deletePersonal/deleteShared.
                  }
                }}
                className="rounded-2xl bg-red-500 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-red-600"
              >
                Eliminar
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
